import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import styled from 'src/ui/library/styled';
import merge from 'lodash.merge';

const styles = (theme, props) => {
  const s = {
    root: {
      display: 'flex',
      marginTop: '24px',
      justifyContent: 'flex-end',
    },
    pagination: {
      width: '100%',
      maxWidth: '272px',
      display: 'flex',
      marginRight: '-19px' /* pagination needs to be flush and icons are SVG */,
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    pages: {
      width: '100%',
      margin: '0px',
      padding: '0px',
      listStyle: 'none',
      display: 'flex',
      position: 'relative',
      top: '2px',
      justifyContent: 'space-around',
      alignItems: 'center',
      cursor: 'pointer',
      '& > li': {
        width: '28px',
        height: '24px',
        margin: '0',
        padding: '0px',
        textAlign: 'center',
        color: `${theme.colors.eyConfidentGrey}`,
      },
      '& > li:hover': {
        borderBottom: `solid 3px ${theme.colors.eyConfidentGrey}`,
      },
    },
    button: {
      margin: 0,
      padding: 0,
      background: 'none',
      border: 'none',
      height: 'calc(100% - 8px)',
      width: '100%',
      cursor: 'pointer',
      fontSize: '12px',
      fontFamily: 'EYInterstate',
    },
    pageActive: {
      borderBottom: `solid 3px ${theme.colors.eyConfidentGrey}`,
    },
    actionArrow: {
      height: '30px',
      width: '30px',
      color: `${theme.colors.eyGrey8}`,
    },
  };
  return props.styleOverrides && props.styleOverrides.TablePagination ? merge(s, props.styleOverrides.TablePagination) : s;
};

class TablePagination extends React.Component {
  static propTypes = {
    /** Styles to be applied to component */
    classes: PropTypes.object,
    /** If provided, the total number of rows per page */
    rowsPerPage: PropTypes.number,
    /** Callback function for page changes */
    onPageChange: PropTypes.func,
    /** Developer styles overrides */
    styleOverrides: PropTypes.object.isRequired,
  };

  getPages = (currentPage, totalPages) => {
    let leftPages = [];
    let rightPages = [];
    let pagesAdded = 0;

    for (let iterationCount = 1; pagesAdded < 4 && iterationCount <= totalPages; iterationCount++) {
      const leftCheck = currentPage - iterationCount;
      const rightCheck = currentPage + iterationCount;

      if (leftCheck > 0) {
        leftPages.unshift(leftCheck);
        pagesAdded++;
      }

      if (rightCheck <= totalPages) {
        rightPages.push(rightCheck);
        pagesAdded++;
      }
    }

    const pages = [...leftPages, currentPage, ...rightPages];
    return pages;
  };

  render() {
    const { classes, page, rowsPerPage, rowCount, onChangePage } = this.props;
    const currentPage = page + 1;
    const totalPages = Math.ceil(rowCount / rowsPerPage);
    const pages = this.getPages(currentPage, totalPages);

    const isPrevDisabled = currentPage <= 1;
    const isNextDisabled = currentPage >= totalPages;

    return (
      <nav aria-label="pagination">
        <div className={classes.root}>
          <div className={classes.pagination}>
            <IconButton
              disabled={isPrevDisabled}
              tabIndex={isPrevDisabled ? -1 : 0}
              aria-label={`currently at ${currentPage}, go to previous page`}
              onClick={onChangePage.bind(null, page - 1)}>
              <KeyboardArrowLeft className={classes.actionArrow} />
            </IconButton>
            <ul className={classes.pages}>
              {pages.map(pageNumber => {
                const pageClass = classNames({ [classes.pageActive]: currentPage === pageNumber });
                return (
                  <li key={pageNumber} className={pageClass}>
                    <button
                      className={classes.button}
                      title={`Goto Page ${pageNumber}`}
                      aria-current={pageNumber === currentPage ? 'page' : 'false'}
                      onClick={onChangePage.bind(null, pageNumber - 1)}>
                      {pageNumber}
                    </button>
                  </li>
                );
              })}
            </ul>
            <IconButton
              disabled={isNextDisabled}
              tabIndex={isNextDisabled ? -1 : 0}
              aria-label={`currently at ${currentPage}, go to next page`}
              onClick={onChangePage.bind(null, page + 1)}>
              <KeyboardArrowRight className={classes.actionArrow} />
            </IconButton>
          </div>
        </div>
      </nav>
    );
  }
}

export default styled(TablePagination)(styles);
